import React, { FC, useMemo, useState } from 'react';

import { skipToken } from '@reduxjs/toolkit/query/react';
import { Controller } from 'react-hook-form';
import TextareaAutosize from 'react-textarea-autosize';

import { ApplicationFlag } from 'models/applications/enum';
import { useGetExaminationResultQuery } from 'redux/api/staff/expert/expert-applications-api';

import Button from '../../../components/common/ui-kit/Button';
import Checkbox from '../../../components/common/ui-kit/Checkbox';
import { InputV2 } from '../../../components/common/ui-kit/Input/view';
import useAppSelector from '../../../hooks/common/useAppSelector';
import useExpertExaminationResultForm from '../../../hooks/expert/useExpertExaminationResultForm';
import { useExpertExaminationResultFormProps } from '../../../hooks/expert/useExpertExaminationResultFormProps';
import { EducationSystemStatus } from '../../../models/applications/applications';
import {
  selectCurrentApplication,
  selectCurrentQuestionnaire,
} from '../../../redux/selectors/staff/applications';
import { createInputV2Props } from '../../../utils/form-helpers';

const ExpertExaminationResultPage: FC = () => {
  const application = useAppSelector(selectCurrentApplication);
  const questionnaire = useAppSelector(selectCurrentQuestionnaire);

  const isFlagApprovedByExpert = useMemo(() => {
    return (
      application?.flags.some(
        (flag: ApplicationFlag) => flag === 'APPROVED_BY_EXPERT'
      ) ?? false
    );
  }, [application?.flags]);

  const { data, refetch } = useGetExaminationResultQuery(
    application?.id ? application.id : skipToken,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const isDisabled = isFlagApprovedByExpert;

  const fullNameEn = (
    surnameEn?: string,
    nameEn?: string,
    patronymicEn?: string
  ) => `${surnameEn ?? ''} ${nameEn ?? ''} ${patronymicEn ?? ''}`.trim();

  const {
    control,
    getValues,
    setValue,
    fields,
    onSubmit,
    formState: { errors },
  } = useExpertExaminationResultForm(
    application?.id ?? 0,
    fullNameEn(
      questionnaire?.generalDataForm.surnameLatin,
      questionnaire?.generalDataForm.nameLatin,
      questionnaire?.generalDataForm.patronymicLatin
    ),
    questionnaire?.generalDataForm.primaryCitizenshipCountry?.title ?? ''
  );

  const { educationLevelSelectOptions, educationSystemStatusOptions } =
    useExpertExaminationResultFormProps();

  const inputV2Props = createInputV2Props(errors);

  const [
    educationSystemStateCommentAllowed,
    setEducationSystemStateCommentAllowed,
  ] = useState(false);

  const currentYear = `/${new Date().getFullYear().toString().slice(-2)}`;

  const normalizeRegistrationNumber = (value: string) => {
    const cleanValue = value.replace(/^Э-/, '').replace(currentYear, '');
    const digits = cleanValue.replace(/\D/g, '').substring(0, 4);

    if (!digits) return '';
    return `Э-${digits}${currentYear}`;
  };

  return (
    <form onSubmit={onSubmit}>
      <div>
        <Controller
          name={fields.registrationNumber.name}
          control={control}
          render={({ field }) => (
            <div className="expert-examination-result-page__block">
              <InputV2
                {...inputV2Props(field)}
                value={field.value || ''}
                onChange={e => {
                  const val = e.target.value;
                  if (!val || val === 'Э-') {
                    field.onChange('');
                    return;
                  }
                  const normalized = normalizeRegistrationNumber(val);
                  field.onChange(normalized);
                }}
                className="expert-examination-result-page__field"
                placeholder="Регистрационный номер"
                maxLength={9}
                disabled={isDisabled}
                markRequired
              />
            </div>
          )}
        />

        <Controller
          name={fields.applicantFullName.name}
          control={control}
          render={({ field }) => (
            <div className="expert-examination-result-page__block">
              <InputV2
                className="expert-examination-result-page__field"
                placeholder="Фамилия и имена обладателя(ьницы)"
                markRequired
                {...inputV2Props(field)}
                disabled={isDisabled}
              />
            </div>
          )}
        />

        <Controller
          name={fields.educationCountryName.name}
          control={control}
          render={({ field }) => (
            <div className="expert-examination-result-page__block">
              <InputV2
                className="expert-examination-result-page__field"
                placeholder="Страна выдачи ИДО"
                markRequired
                {...inputV2Props(field)}
                disabled={isDisabled}
              />
            </div>
          )}
        />

        <Controller
          name={fields.educationSystemStatus.name}
          control={control}
          render={({ field }) => (
            <div className="expert-examination-result-page__block">
              <InputV2
                hidden={true}
                placeholder="Образовательная организация, выдавшая представленные ИДО:"
              />
              {educationSystemStatusOptions.map(opt => (
                <>
                  <Checkbox
                    key={opt.value}
                    className="expert-examination-result-page__field"
                    text={opt.label}
                    onChange={e => {
                      const newValue: EducationSystemStatus | undefined = e
                        .target.checked
                        ? opt.value
                        : undefined;
                      field.onChange(newValue);
                      setEducationSystemStateCommentAllowed(
                        newValue ===
                          EducationSystemStatus.PART_OF_EDUCATION_SYSTEM
                      );
                    }}
                    checked={field.value === opt.value}
                    disabled={isDisabled}
                  />
                  {opt.value ===
                    EducationSystemStatus.PART_OF_EDUCATION_SYSTEM && (
                    <Controller
                      name={fields.educationSystemStatusComment.name}
                      control={control}
                      render={({ field }) => (
                        <TextareaAutosize
                          className="expert-examination-result-page__comment-input"
                          {...field}
                          placeholder="Комментарий"
                          maxLength={2048}
                          disabled={!educationSystemStateCommentAllowed}
                        />
                      )}
                    />
                  )}
                </>
              ))}
            </div>
          )}
        />

        <Controller
          name={fields.needNegotiations.name}
          control={control}
          render={({ field }) => (
            <div className="expert-examination-result-page__block">
              <InputV2 hidden={true} placeholder="С обладателем(ьницей) ИДО:" />
              <Checkbox
                className="expert-examination-result-page__field"
                text="Можно вести дальнейшие переговоры об обучении в СПбПУ"
                onChange={e => {
                  const newValue: boolean | undefined = e.target.checked
                    ? true
                    : undefined;

                  field.onChange(newValue);
                }}
                checked={field.value === true}
                disabled={isDisabled}
              />
              <Checkbox
                className="expert-examination-result-page__field"
                text="Дальнейшие переговоры об обучении в СПбПУ нецелесообразны"
                onChange={e => {
                  const newValue: boolean | undefined = e.target.checked
                    ? false
                    : undefined;

                  field.onChange(newValue);
                }}
                checked={field.value === false}
                disabled={isDisabled}
              />
            </div>
          )}
        />

        <Controller
          name={fields.educationLevelsInfo.name}
          control={control}
          render={({ field }) => (
            <div className="expert-examination-result-page__block">
              <InputV2
                hidden={true}
                placeholder="Обладатель(ница) документов может претендовать на отмеченное или более
          низкий уровень образования:"
              />
              {educationLevelSelectOptions.map(opt => (
                <>
                  <Checkbox
                    key={opt.value}
                    className="expert-examination-result-page__field"
                    text={opt.label}
                    onChange={e => {
                      const newValue = {
                        ...field.value,
                        [opt.value]: {
                          ...field.value[opt.value],
                          checked: e.target.checked,
                        },
                      };
                      field.onChange(newValue);
                    }}
                    checked={field.value[opt.value].checked === true}
                    disabled={isDisabled}
                  />
                  <TextareaAutosize
                    className="expert-examination-result-page__comment-input"
                    placeholder="Комментарии"
                    maxLength={100}
                    value={field.value[opt.value]?.comment || ''}
                    onChange={e => {
                      const newValue = {
                        ...field.value,
                        [opt.value]: {
                          ...field.value[opt.value],
                          comment: e.target.value,
                        },
                      };
                      field.onChange(newValue);
                    }}
                    disabled={isDisabled}
                  />
                </>
              ))}
            </div>
          )}
        />

        <Controller
          name={fields.disciplineTransferAllowed.name}
          control={control}
          render={({ field }) => (
            <div className="expert-examination-result-page__block">
              <InputV2 hidden={true} placeholder="Особые отметки:" />
              <Checkbox
                className="expert-examination-result-page__field"
                text="Возможен перезачёт дисциплин с последующим переводом документообладателя(ьницы) на старшие курсы программы обучения"
                {...(field as object)}
                checked={field.value === true}
                disabled={isDisabled}
              />
            </div>
          )}
        />
        <Controller
          name={fields.comments.name}
          control={control}
          render={({ field }) => (
            <div className="expert-examination-result-page__block">
              <TextareaAutosize
                className="expert-examination-result-page__comment-input"
                {...(field as object)}
                placeholder="Комментарии"
                maxLength={2048}
                disabled={isDisabled}
              />
            </div>
          )}
        />
        <Controller
          name={fields.formationDate.name}
          control={control}
          render={({ field }) => (
            <div className="expert-examination-result-page__block">
              <InputV2
                type="date"
                className="expert-examination-result-page__field"
                markRequired
                placeholder="Дата составления заключения"
                {...inputV2Props(field)}
                disabled={isDisabled}
              />
            </div>
          )}
        />
        <Button
          theme="success"
          className="expert-examination-result-page__send-button"
          disabled={isDisabled}
        >
          Сохранить результаты предварительной экспертизы
        </Button>
      </div>
    </form>
  );
};

export default ExpertExaminationResultPage;
